import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  competingPairList,
  type CompetingPairList,
  type CompetingPairListRow
} from '@taptimize/shared/dist/review/cannibalization-card';
import { CompetingPagesService } from '../../services/competing-pages.service';

/**
 * Inventory → Competing pages: every pair of the site's pages that compete for the same searches,
 * with the verdict the review surface's Cannibalization card gives it. Read-only.
 *
 * The rows, their wording and their order are `competingPairList` (shared/src/review/
 * cannibalization-card.ts), the same verdicts `/review`, the agent export and the Issues queue read,
 * so the four cannot disagree about a pair. Nothing is decided here. The payload is the card's
 * (`GET /urls/competing-pages`, cached per domain by the service).
 */
@Component({
  selector: 'app-inventory-competing-pages',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './inventory-competing-pages.html',
  styleUrl: './inventory-competing-pages.css'
})
export class InventoryCompetingPagesComponent implements OnChanges, OnDestroy {
  @Input() domain = '';

  loading = false;
  failed = false;
  list: CompetingPairList | null = null;

  private subscription?: Subscription;

  constructor(private readonly competingPages: CompetingPagesService) {}

  ngOnChanges(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private load(): void {
    this.subscription?.unsubscribe();
    this.list = null;
    this.failed = false;
    const domain = (this.domain || '').trim();
    if (!domain) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.subscription = this.competingPages.getForDomain(domain).subscribe((payload) => {
      this.loading = false;
      this.failed = !payload;
      this.list = payload ? competingPairList(payload) : null;
    });
  }

  /** URL detail, where the page's Cannibalization card is. */
  detailLink(url: string): string[] {
    return ['/inventory', encodeURIComponent(url.trim())];
  }

  trackRow(_index: number, row: CompetingPairListRow): string {
    return `${row.first.pageId}|${row.second.pageId}`;
  }
}
